import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ENTITY_TYPE } from '../core/const/entity-type';
import { EntityOption } from '../entity-options-menu/entity-option';
import { FilterMetadata } from 'primeng/api';
import { PARAM_MATCH_MODE } from '../core/grid/param-match-mode';
import { LastEntityItemChecked } from '../entity-list/base-entity-item/last-entity-item-checked';
import { DIALOG_VISIBILITY } from '../core/const/dialog-visibility';
import { SAVE_OPTION } from './save-option';
import { KeyId } from './key-id';
import { ToggledTable } from './toggled-table';
import { EffectiveRole } from './effective-role';

@Injectable({
  providedIn: 'root'
})
export class ServiceAccessDataService {

  private _selectedIds: { [key: string]: string[] } = {
    connectUserId: [],
    tenantId: [],
    userGroupInvariantKey: [],
    companyGroupInvariantKey: [],
    roleInvariantKey: []
  };

  private _pendingIds: string[] = [];

  private _lastEntityItemChecked: LastEntityItemChecked = {
    idKey: '',
    id: ''
  }

  private _connectUserIdsBelongToACustomer: string[] = [];

  private _subjectTypeSelected: string = ENTITY_TYPE.user;
  private _contextTypeSelected: string = ENTITY_TYPE.company;
  private _assignmentTypeSelected: string = ENTITY_TYPE.role;

  private _entityOptionTypeSelected: string = ENTITY_TYPE.none;

  private _currentEntityOption: EntityOption = new EntityOption();
  private _oldEntityOption: EntityOption = new EntityOption();

  private _lastEfectiveRolesChanged: EffectiveRole[] = [];

  private _subjectEntityOption = new Subject<EntityOption>();
  public subjectEntityOption$: Observable<EntityOption> = this._subjectEntityOption.asObservable();

  private _contextEntityOption = new Subject<EntityOption>();
  public contextEntityOption$: Observable<EntityOption> = this._contextEntityOption.asObservable();

  private _assignmentEntityOption = new Subject<EntityOption>();
  public assignmentEntityOption$: Observable<EntityOption> = this._assignmentEntityOption.asObservable();

  private _setFocusOn = new Subject<string>();
  public setFocusOn$: Observable<string> = this._setFocusOn.asObservable();

  private _goToRootContext = new Subject<boolean>();
  public goToRootContext$: Observable<boolean> = this._goToRootContext.asObservable();

  private _change = new Subject<{ [key: string]: FilterMetadata }>();
  public change$: Observable<{ [key: string]: FilterMetadata }> = this._change.asObservable();

  private _changeOnCreate = new Subject<{ [key: string]: FilterMetadata }>();
  public changeOnCreate$: Observable<{ [key: string]: FilterMetadata }> = this._changeOnCreate.asObservable();

  private _refresh = new Subject<{ [key: string]: FilterMetadata }>();
  public refresh$: Observable<{ [key: string]: FilterMetadata }> = this._refresh.asObservable();

  private _refreshOnTerminateSubscriber = new Subject<boolean>();
  public refreshOnTerminateSubscriber$: Observable<boolean> = this._refreshOnTerminateSubscriber.asObservable();

  private _resetCheckboxes = new Subject<boolean>();
  public resetCheckboxes$: Observable<boolean> = this._resetCheckboxes.asObservable();

  private _resetRoleGroupCheckboxes = new Subject<boolean>();
  public resetRoleGroupCheckboxes$: Observable<boolean> = this._resetRoleGroupCheckboxes.asObservable();

  private _save = new Subject<string>();
  public save$: Observable<string> = this._save.asObservable();


  private _limitWarningDialog = new Subject<string>();
  public limitWarningDialog$: Observable<string> = this._limitWarningDialog.asObservable();

  private _removeUserFromUserGroupDialog = new Subject<string>();
  public removeUserFromUserGroupDialog$: Observable<string> = this._removeUserFromUserGroupDialog.asObservable();

  private _activateServiceDialog = new Subject<string>();
  public activateServiceDialog$: Observable<string> = this._activateServiceDialog.asObservable();


  private _resetLastEntityItemChecked = new Subject<LastEntityItemChecked>();
  public resetLastEntityItemChecked$: Observable<LastEntityItemChecked> = this._resetLastEntityItemChecked.asObservable();

  private _adjustTableHeight = new Subject<ToggledTable>();
  public adjustTableHeight$: Observable<ToggledTable> = this._adjustTableHeight.asObservable();

  private _refreshUsersList = new Subject<boolean>();
  public refreshUsersList$: Observable<boolean> = this._refreshUsersList.asObservable();

  private _refreshPendingUsers = new Subject<boolean>();
  public refreshPendingUsers$: Observable<boolean> = this._refreshPendingUsers.asObservable();

  private _refreshUsersWithoutAccess = new Subject<boolean>();
  public refreshUsersWithoutAccess$: Observable<boolean> = this._refreshUsersWithoutAccess.asObservable();

  private _refreshUsersWithoutAccessOnAddSelected = new Subject<boolean>();
  public refreshUsersWithoutAccessOnAddSelected$: Observable<boolean> = this._refreshUsersWithoutAccessOnAddSelected.asObservable();

  setEntityOptionSelected(option: EntityOption): void {
    this._entityOptionTypeSelected = option.type;
    this._currentEntityOption = option;
    this._oldEntityOption = option;
  }

  getEntityOptionTypeSelected(): string {
    return this._entityOptionTypeSelected;
  }

  getCurrentEntityOption(): EntityOption {
    return this._currentEntityOption;
  }

  getOldEntityOption(): EntityOption {
    return this._oldEntityOption;
  }

  getSubjectTypeSelected(): string {
    return this._subjectTypeSelected;
  }

  setSubjectTypeSelected(entityType: string): void {
    this._subjectTypeSelected = entityType;
  }

  getContextTypeSelected(): string {
    return this._contextTypeSelected;
  }

  setContextTypeSelected(entityType: string): void {
    this._contextTypeSelected = entityType;
  }

  getAssignmentTypeSelected(): string {
    return this._assignmentTypeSelected;
  }

  setAssignmentTypeSelected(entityType: string): void {
    this._assignmentTypeSelected = entityType;
  }

  getLastEffectiveRolesChanged(): EffectiveRole[] {
    return this._lastEfectiveRolesChanged;
  }

  setLastEffectiveRolesChanged(effectiveRoles: EffectiveRole[]): void {
    this._lastEfectiveRolesChanged = effectiveRoles;
  }

  setSubjectOption(option: EntityOption): void {
    this._subjectTypeSelected = option.type;
    this._subjectEntityOption.next(option);
  }

  setContextOption(option: EntityOption): void {
    this._contextTypeSelected = option.type;
    this._contextEntityOption.next(option);
  }

  setAssignmentOption(option: EntityOption): void {
    this._assignmentTypeSelected = option.type;
    this._assignmentEntityOption.next(option);
  }

  getSelectedIds(field: string): string[] {
    return this._selectedIds[field];
  }

  refreshUsersWithoutAccess(): void {
    this._refreshUsersWithoutAccess.next(true);
  }

  refreshUsersWithoutAccessOnAddSelected(): void {
    this._refreshUsersWithoutAccessOnAddSelected.next(true);
  }

  getPendingIds(): string[] {
    return this._pendingIds;
  }

  addToPendingIds(id: string): void {
    this._pendingIds.push(id);
  }

  removeFromPendingIds(id: string): void {
    this._pendingIds = this._pendingIds.filter(value => { return value !== id; });
  }

  isInPendingIds(id: string): boolean {
    return this._pendingIds.filter(value => { return value === id; }).length > 0;
  }

  removeFromPendingIdsAndRefreshTheUserList(id: string): void {
    this._pendingIds = this._pendingIds.filter(value => { return value !== id; });
    this._refreshPendingUsers.next(true);
  }

  addConnectUserIdToBelongsToACustomerArray(connectUserId: string): void {
    this._connectUserIdsBelongToACustomer.push(connectUserId);
  }

  removeConnectUserIdFromBelongsToACustomerArray(connectUserId: string): void {
    this._connectUserIdsBelongToACustomer = this._connectUserIdsBelongToACustomer.filter(value => value !== connectUserId);
  }

  getConnectUserIdsBelongsToACustomerArray(): string[] {
    return this._connectUserIdsBelongToACustomer;
  }

  isIdInConnectUserIdsBelongsToACustomerArray(connectUserId: string): boolean {
    return this._connectUserIdsBelongToACustomer.filter(value => value === connectUserId).length > 0;
  }

  resetConnectUserIdsBelongsToACustomerArray(): void {
    this._connectUserIdsBelongToACustomer = [];
  }

  refreshUserList(): void {
    this._refreshUsersList.next(true)
  }

  showLimitWarningDialog(): boolean {
    let has = false;
    let count = 0;
    const obj = this._selectedIds;
    const keys = Object.keys(obj);
    for (let i = 0, len = keys.length; i < len; i++) {
      if (obj[keys[i]].length > 0) {
        count += 1;
      }
    }
    if (count >= 3) {
      has = true;
    }
    return has;
  }

  hasThreeSelections(): boolean {
    let selectionCount = 0;
    // Check non-role keys
    if (this._selectedIds['connectUserId'].length > 0) selectionCount++;
    if (this._selectedIds['tenantId'].length > 0) selectionCount++;
    if (this._selectedIds['userGroupInvariantKey'].length > 0) selectionCount++;
    if (this._selectedIds['companyGroupInvariantKey'].length > 0) selectionCount++;

    // Check role keys (count as 1 if both have values)
    if (this._selectedIds['roleInvariantKey'].length > 0) {
      selectionCount++;
    }

    return selectionCount >= 3;
  }

  clearSelectionsFor(type: string, idKey: string): void {
    this._entityOptionTypeSelected = type;
    this._selectedIds[idKey] = [];
    const value = this.getFilters();
    this._refresh.next(value);
  }

  save(): void {
    this._save.next(SAVE_OPTION.save);
  }

  refresh(): void {
    const entityType = this._entityOptionTypeSelected;
    const assignmentTypeSelected = this._assignmentTypeSelected;
    switch (entityType) {
      case ENTITY_TYPE.user:
      case ENTITY_TYPE.supportUser:
        this._selectedIds['userGroupInvariantKey'] = [];
        this._selectedIds['connectUserId'] = [];
        if (assignmentTypeSelected !== ENTITY_TYPE.systemRoleGroup) {
          this._selectedIds['roleInvariantKey'] = [];
        }
        break;
      case ENTITY_TYPE.userGroup: this._selectedIds['connectUserId'] = [];
        if (assignmentTypeSelected !== ENTITY_TYPE.systemRoleGroup) {
          this._selectedIds['roleInvariantKey'] = [];
        }
        break;
      case ENTITY_TYPE.companyGroup: {
        this._selectedIds['tenantId'] = [];
        if (assignmentTypeSelected !== ENTITY_TYPE.systemRoleGroup) {
          this._selectedIds['roleInvariantKey'] = [];
        }
        break;
      }
      case ENTITY_TYPE.company:
      case ENTITY_TYPE.clientCompany:
        this._selectedIds['tenantId'] = [];
        this._selectedIds['companyGroupInvariantKey'] = [];
        if (assignmentTypeSelected !== ENTITY_TYPE.systemRoleGroup) {
          this._selectedIds['roleInvariantKey'] = [];
        }
        break;
      case ENTITY_TYPE.customer:
        this._selectedIds['tenantId'] = [];
        this._selectedIds['companyGroupInvariantKey'] = [];
        this._selectedIds['roleInvariantKey'] = [];
        break;
      case ENTITY_TYPE.role:
      case ENTITY_TYPE.roleGroup:
      case ENTITY_TYPE.systemRoleGroup:
        this._selectedIds['roleInvariantKey'] = [];
        break;
    }

    const value = this.getFilters();
    if (entityType === ENTITY_TYPE.companyGroup || entityType === ENTITY_TYPE.company ||
      entityType === ENTITY_TYPE.clientCompany || entityType === ENTITY_TYPE.customer) {
      this._resetRoleGroupCheckboxes.next(true);
    }
    this._refresh.next(value);
  }

  goToRootContext(): void {
    this._goToRootContext.next(true);
  }

  setFocusOn(type: string): void {
    this._setFocusOn.next(type);
  }

  hideRemoveUserFromUserGroupDialog(): void {
    this._removeUserFromUserGroupDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideAndRefreshRemoveUserFromUserGroupDialog(): void {
    this._removeUserFromUserGroupDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  hideActivateServiceDialog(): void {
    this._activateServiceDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideAndRefreshActivateServiceDialog(): void {
    this._activateServiceDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  hideLimitWarningDialog(): void {
    this._limitWarningDialog.next(DIALOG_VISIBILITY.hide);
  }

  change(): void {
    const value = this.getFilters();
    this._change.next(value);
  }

  add(idKey: string, id: string): void {
    this._selectedIds[idKey].push(id);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
    const value = this.getFilters();
    this._change.next(value);
  }

  addOnCreate(idKey: string, id: string): void {
    this._selectedIds[idKey].push(id);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
    const value = this.getFilters();
    this._changeOnCreate.next(value);
  }

  addOnCreateItems(arr: KeyId[]): void {
    for (let i = 0, len = arr.length; i < len; i++) {
      this._selectedIds[arr[i].idKey].push(arr[i].id);
    }
    this._lastEntityItemChecked = { id: arr[arr.length - 1].id, idKey: arr[arr.length - 1].idKey };
    const value = this.getFilters();
    this._changeOnCreate.next(value);
  }

  addWithoutTriggerChange(idKey: string, id: string): void {
    this._selectedIds[idKey].push(id);
    this._lastEntityItemChecked = { id: id, idKey: idKey };
  }

  remove(idKey: string, id: string): void {
    this._selectedIds[idKey] = this._selectedIds[idKey].filter(value => { return value !== id; });
    const value = this.getFilters();
    this._change.next(value);
  }

  removeWithoutTriggerChange(idKey: string, id: string): void {
    const arr = this._selectedIds[idKey].filter(value => { return value === id; });
    if (arr.length > 0) {
      this._selectedIds[idKey] = this._selectedIds[idKey].filter(value => { return value !== id; });
    }
  }

  resetAndChangeforMain(): void {
    this._selectedIds = {
      connectUserId: [],
      tenantId: [],
      userGroupInvariantKey: [],
      companyGroupInvariantKey: [],
      roleInvariantKey: []
    }

    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    const data = this.getFilters();
    this._change.next(data);
  }

  reset(idKey1?: string, id1?: string, idKey2?: string, id2?: string): void {
    this._selectedIds = {
      connectUserId: [],
      tenantId: [],
      userGroupInvariantKey: [],
      companyGroupInvariantKey: [],
      roleInvariantKey: [],
    }

    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    this._resetCheckboxes.next(true);
    if (idKey1 !== undefined && id1 !== undefined) {
      if (this._selectedIds[idKey1].includes(id1) === false) {
        this._selectedIds[idKey1].push(id1);
      }
      if (idKey2 !== undefined && id2 !== undefined) {
        if (this._selectedIds[idKey2].includes(id2) === false) {
          this._selectedIds[idKey2].push(id2);
        }
      }
      const data = this.getFilters();
      this._change.next(data);
    }
  }

  resetOnTerminateSubscriber(): void {
    this.resetSelectedIdsForMain();
    this._entityOptionTypeSelected = ENTITY_TYPE.none;
    this._subjectTypeSelected = ENTITY_TYPE.user;
    this._contextTypeSelected = ENTITY_TYPE.company;
    this._assignmentTypeSelected = ENTITY_TYPE.role;
    this._refreshOnTerminateSubscriber.next(true);
  }

  resetSelectedIdsForMain(): void {
    this._selectedIds = {
      connectUserId: [],
      tenantId: [],
      userGroupInvariantKey: [],
      companyGroupInvariantKey: [],
      roleInvariantKey: []
    }
  }

  resetLastEntityItemChecked(): void {
    const last = this._lastEntityItemChecked;
    const arr = this._selectedIds[last.idKey].filter(value => { return value === last.id; });
    if (arr.length > 0) {
      this._selectedIds[last.idKey] = this._selectedIds[last.idKey].filter(value => { return value !== last.id; });
      this._resetLastEntityItemChecked.next(last);
      this._lastEntityItemChecked = { id: '', idKey: '' };
      const value = this.getFilters();
      this._change.next(value);
    }
  }

  reloadMainServiceAccess(): void {
    const value = this.getFilters();
    this._change.next(value);
  }


  getFilters(): { [key: string]: FilterMetadata } {
    const filters: { [key: string]: FilterMetadata } = {};
    const map = this.getKeysWithValue();
    const keys = Object.keys(map);
    for (let i = 0, len = keys.length; i < len; i++) {
      const key: string = keys[i];
      const res = map[key].filter(value => {
        return value !== undefined;
      });
      filters[key] = { matchMode: PARAM_MATCH_MODE.in, value: `[${res.toString().replace(/,/g, '|')}]` };
    }
    return filters
  }

  hasFilters(): boolean {
    const map = this.getKeysWithValue();
    return Object.keys(map).length > 0;
  }

  adjustHeightForTable(name: string, isExpanded: boolean): void {
    this._adjustTableHeight.next({ name: name, isExpanded: isExpanded });
  }

  private getKeysWithValue(): { [key: string]: any[] } {
    let map: { [key: string]: any[] } = {};
    const keys = Object.keys(this._selectedIds);
    for (let i = 0, len = keys.length; i < len; i++) {
      const arr = this._selectedIds[keys[i]];
      if (arr.length > 0) {
        map[keys[i]] = arr;
      }
    }
    return map;
  }

}
